/**
 * Centralized Logging Service for RideQuest Frontend
 *
 * Provides structured, leveled logging with OpenTelemetry trace correlation.
 * Replaces scattered console.log/console.error calls with a consistent API.
 *
 * Log Levels (ordered by severity):
 * - TRACE: Very detailed internal state, disabled in production
 * - DEBUG: Debugging info useful during development (function entry/exit, state changes)
 * - INFO:  Normal operational events (login success, booking created, app started)
 * - WARN:  Potentially harmful situations (validation failures, missing data, retries)
 * - ERROR: Error events that may still allow the app to continue
 * - FATAL: Critical errors causing application-level failure
 *
 * Usage:
 *   import logger from '../services/logger';
 *   logger.info('Booking created', { bookingId, serviceKey });
 *   logger.error('Failed to create booking', error, { serviceKey });
 */

import { trace, SpanStatusCode } from '@opentelemetry/api';

/** Log severity levels matching OpenTelemetry severity numbering */
export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  FATAL = 5,
}

/** Structured context data attached to log entries */
interface LogContext {
  [key: string]: unknown;
}

/**
 * Structured log entry format for consistent output and potential backend shipping.
 */
interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  trace_id?: string;
  span_id?: string;
  [key: string]: unknown;
}

class Logger {
  private minLevel: LogLevel;

  constructor() {
    // Set minimum log level based on environment:
    // Development: DEBUG and above (skip TRACE)
    // Production: INFO and above (skip TRACE and DEBUG)
    this.minLevel = import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO;
  }

  /**
   * TRACE: Very detailed debugging information.
   * Only visible in development with explicit trace-level configuration.
   * Use for: internal state dumps, loop iterations, detailed data flow
   */
  trace(message: string, ctx?: LogContext): void {
    this.log(LogLevel.TRACE, message, ctx);
  }

  /**
   * DEBUG: Information useful during development and debugging.
   * Filtered out in production to reduce noise.
   * Use for: function entry/exit, intermediate values, cache hits/misses
   */
  debug(message: string, ctx?: LogContext): void {
    this.log(LogLevel.DEBUG, message, ctx);
  }

  /**
   * INFO: Normal operational events worth recording.
   * Use for: successful operations (login, booking created), state transitions
   */
  info(message: string, ctx?: LogContext): void {
    this.log(LogLevel.INFO, message, ctx);
  }

  /**
   * WARN: Potentially harmful situations that don't prevent operation.
   * Use for: validation failures, missing optional data, fallback behavior
   */
  warn(message: string, ctx?: LogContext): void {
    this.log(LogLevel.WARN, message, ctx);
  }

  /**
   * ERROR: Error events that may still allow the application to continue.
   * Automatically records the exception on the active OpenTelemetry span.
   * Use for: caught exceptions, failed API calls, operation failures
   */
  error(message: string, error?: Error, ctx?: LogContext): void {
    const errorCtx = error
      ? {
          ...ctx,
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        }
      : ctx;

    this.log(LogLevel.ERROR, message, errorCtx);

    // Attach error to the active OpenTelemetry span for trace visibility
    const span = trace.getActiveSpan();
    if (span && error) {
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    }
  }

  /**
   * FATAL: Critical errors that may cause application failure.
   * Use for: unrecoverable state, render failures, missing critical dependencies
   */
  fatal(message: string, error?: Error, ctx?: LogContext): void {
    const errorCtx = error
      ? {
          ...ctx,
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        }
      : ctx;

    this.log(LogLevel.FATAL, message, errorCtx);

    const span = trace.getActiveSpan();
    if (span && error) {
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: `FATAL: ${error.message}` });
    }
  }

  /**
   * Internal: Format and output a log entry.
   * Enriches entries with timestamp and trace correlation IDs when available.
   */
  private log(level: LogLevel, message: string, ctx?: LogContext): void {
    // Skip logs below minimum level
    if (level < this.minLevel) return;

    const levelName = LogLevel[level];

    // Build structured log entry
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: levelName,
      message,
      ...ctx,
    };

    // Enrich with trace correlation IDs from the active span
    const span = trace.getActiveSpan();
    if (span) {
      const spanCtx = span.spanContext();
      entry.trace_id = spanCtx.traceId;
      entry.span_id = spanCtx.spanId;
    }

    // Route to appropriate console method based on severity
    const formattedPrefix = `[${levelName}]`;
    switch (level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        console.debug(formattedPrefix, message, ctx || '');
        break;
      case LogLevel.INFO:
        console.info(formattedPrefix, message, ctx || '');
        break;
      case LogLevel.WARN:
        console.warn(formattedPrefix, message, ctx || '');
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(formattedPrefix, message, ctx || '');
        break;
    }
  }
}

/** Singleton logger instance - import and use directly across the application */
export const logger = new Logger();
export default logger;
